/* eslint-disable camelcase */
import {
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';
import {
  useNavigate,
  useLocation,
} from 'react-router';
import {
  usePrevious,
} from 'web-hooks';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getUiState,
} from 'web-selectors';

import {
  decodeParam,
} from 'web-utils';

import {
  uiActions,
} from 'web-actions';

import {
  Icon,
  Spinner,
  NotFound,
  NavigationTabs,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

import Messages from './Messages';
import Footer from './Footer';
import * as S from './styled';

type Props = {
  id: string,
  category: string,
};

const Chat = ({ id, category }: Props) => {
  const navigate = useNavigate();
  const prevCategory = usePrevious(category);
  const mapState = useCallback(state => ({
    pro: coreSelectors.getClientPro(state),
    user: coreSelectors.getProfile(state),
    readStatus: getStatus(
      state,
      [
        'messages.requests.fetchMessages.status',
      ],
    ),
    messages: coreSelectors.getMessages(
      id,
      category,
    )(state),
    ...getUiState([
      'openedTooltips',
    ])(state),
    conversations: coreSelectors.getConversations(state),
  }), [id, category]);
  const {
    pro,
    user,
    messages,
    readStatus,
    conversations,
    openedTooltips,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const location = useLocation();
  const params = decodeParam(location.search);
  const isPro = user.is_pro;
  const conversation = conversations.find(
    c => c.id === Number(id),
  );

  const tabs = useMemo(() => ([
    {
      name: 'General',
      to: `/messages/${id}/1${location.search}`,
      unread: conversation ? conversation.unreadCategories?.['1'].length > 0 : false,
    },
    {
      name: 'Exercise',
      to: `/messages/${id}/2${location.search}`,
      unread: conversation ? conversation.unreadCategories?.['2'].length > 0 : false,
    },
    {
      name: 'Nutrition',
      to: `/messages/${id}/3${location.search}`,
      unread: conversation ? conversation.unreadCategories?.['3'].length > 0 : false,
    },
  ]), [conversation]);

  useEffect(() => {
    if (prevCategory && category !== prevCategory) {
      dispatch({
        type: 'GC_ANALYTICS',
        eventKey: 'switched_message_tabs',
        attrs: {
          id,
          from: tabs[Number(prevCategory) - 1].name,
          to: tabs[Number(category) - 1].name,
        },
      });
    }
  }, [category, prevCategory]);

  useEffect(() => {
    if (id && id !== 'new') {
      dispatch(
        coreActions.requestMessages({
          id,
          qs: {
            page: 1,
            per_page: 50,
            message_category: category,
          },
        }),
      );
    } else if (!isPro && conversations.length) {
      const newUrl = `/messages/${conversations[0].id}/1`;
      navigate(newUrl);
    } else if (!params.recipient) {
      navigate('/messages');
    }
  }, [id, category]);

  const handleSubmit = (values: any, actions: any) => {
    dispatch(
      uiActions.requestSendMessage({
        payload: {
          ...values,
          recipient_id: isPro ? (params.recipient || conversation?.recipient?.user_id) : pro?.id,
          message_category: category,
        },
        conversationId: id,
        callbacks: {
          ...actions,
          onSuccess: (result: any) => {
            actions.onSuccess(result);
            if (!user.user_settings.tutorial_steps.includes('send_message')) {
              dispatch(
                coreActions.requestMarkTutotialStepsComplite({
                  steps: ['send_message'],
                }),
              );
            }

            if (id === 'new' && result?.conversation_id) {
              navigate(`/messages/${result.conversation_id}/${result.message_category}`);
            }
          },
        },
      }),
    );
  };

  const handleDelete = () => {
    const onConfirmAction = coreActions.requestDeleteEntity({
      id: Number(id),
      resourceType: 'conversations',
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete messages',
          bodyText: 'Are you sure you want to delete this conversation?',
          confirmText: 'Delete',
        },
        onSuccess: () => {
          navigate('/messages');
        },
        onConfirmAction,
      }),
    );
  };

  const onMarkRead = () => {
    if (conversation?.has_unread_messages) {
      const ids = messages
        .filter(
          message => !message.is_read,
        )
        .map(message => message.id);
      dispatch(
        coreActions.requestMarkReadMessages({
          ids,
          conversationId: id,
        }),
      );
    }
  };

  return (
    <S.Chat>
      <S.Header>
        {(id === 'new' || !isPro) ? (
          <div />
        ) : (
          <Flex
            width="44px"
            height="44px"
            align="center"
            justify="center"
            clickable
            onClick={handleDelete}
          >
            <Icon
              size="18px"
              type="far fa-trash-alt"
              color="danger"
            />
          </Flex>
        )}
        <NavigationTabs
          tabs={tabs}
        />
      </S.Header>
      {!readStatus.pending && !readStatus.failed && (
        <S.Body>
          <Messages
            user={user}
            messages={messages}
            onMarkRead={onMarkRead}
          />
        </S.Body>
      )}

      {readStatus.pending && (
        <Spinner />
      )}

      {readStatus.failed && (
        <NotFound title="Error" />
      )}
      <Footer
        showTooltip={id === 'new'}
        onSubmit={handleSubmit}
      />
    </S.Chat>
  );
};

export default Chat;
